import React from "react";

import LineSpacing from "@svg/modifiers/line-spacing";

import { LINE_HEIGHT_OPTIONS } from "@/features/react-editor/components/modifiers/constants";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface LineHeightSelectorProps {
  lineHeight: number | null;
  fontSize: number | null;
  onSelect: (lineHeight: number | null) => void;
  placeholder: string;
}

/**
 * Map computed pixel lineHeight value to the closest LINE_HEIGHT_OPTIONS value
 */
function mapPixelLineHeightToOption(
  pixelLineHeight: number,
  fontSize: number,
): number {
  if (fontSize <= 0) {
    return 1.5;
  }

  const multiplier = pixelLineHeight / fontSize;

  let closestOption = LINE_HEIGHT_OPTIONS[0]!;
  let closestDistance = Math.abs(multiplier - closestOption.value);

  for (const option of LINE_HEIGHT_OPTIONS) {
    const distance = Math.abs(multiplier - option.value);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestOption = option;
    }
  }

  return closestOption.value;
}

const LineHeightSelector: React.FC<LineHeightSelectorProps> = ({
  lineHeight,
  fontSize,
  onSelect,
  placeholder,
}) => {
  const mappedLineHeight = React.useMemo(() => {
    if (lineHeight && fontSize) {
      return mapPixelLineHeightToOption(lineHeight, fontSize);
    }
    return null;
  }, [lineHeight, fontSize]);

  const [selectedLineHeight, setSelectedLineHeight] = useOverridableState(
    mappedLineHeight,
    onSelect,
  );

  const currentOption = LINE_HEIGHT_OPTIONS.find(
    (option) => option.value === selectedLineHeight,
  );

  const lineHeightOptions = LINE_HEIGHT_OPTIONS.map((height) => ({
    value: height.value.toString(),
    displayValue: height.displayValue,
    label: (
      <MenuItem
        variant="default"
        size="sm"
        selected={selectedLineHeight === height.value}
      >
        {height.label}
      </MenuItem>
    ),
  }));

  return (
    <div className="w-full overflow-hidden">
      <Combobox.Root
        options={lineHeightOptions}
        value={selectedLineHeight?.toString() ?? undefined}
        onChange={(value) => setSelectedLineHeight(Number(value))}
      >
        <Combobox.Trigger>
          <Combobox.SelectionButton
            layoutClassName="w-full"
            title={currentOption?.displayValue ?? placeholder}
            isPlaceholder={!selectedLineHeight}
            size="sm"
            startEnhancer={<LineSpacing />}
            titleAlignment="start"
            endEnhancer={<ChevronDown size={12} className="text-muted" />}
          />
        </Combobox.Trigger>
        <Combobox.Popover align="center" layoutClassName="w-[160px]">
          <Combobox.Content
            areOptionsSearchable
            emptySearchMessage="No matches found"
          />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default LineHeightSelector;
